import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';

const { FiX, FiPlay, FiCpu, FiBook, FiZap, FiCheckSquare, FiClock, FiTag, FiBarChart2, FiCalendar, FiBookOpen, FiShield, FiLoader } = FiIcons;

const formatIcon = {
  Video: FiPlay,
  Interactive: FiCpu,
  Course: FiBook,
  'Micro-learning': FiZap,
  Quiz: FiCheckSquare
};

const getEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/);
  if (ytMatch && ytMatch[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch && vimeoMatch[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch && driveMatch[1]) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  if (url.includes('app.dadan.io')) return url;
  if (url.includes('embed') || url.endsWith('.mp4')) return url;
  return null;
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
    <SafeIcon icon={icon} className="text-slate-400 flex-shrink-0 mt-0.5" />
    <div>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
      <p className="text-sm text-slate-800 font-semibold mt-0.5">{value}</p>
    </div>
  </div>
);

const ModulePreviewModal = ({ module, onClose, onEdit, onAssign }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (module) {
      dataFetch('read/campaigns').then(res => {
        if (res?.status === 'success') {
          const allCamps = res.data || [];
          setCampaigns(allCamps.filter(c => (module.assignedCampaigns || []).includes(c.id)));
        }
        setLoading(false);
      });
    }
  }, [module]);

  if (!module) return null;

  const Icon = formatIcon[module.deliveryFormat] || FiBook;
  const embedUrl = module.deliveryFormat === 'Video' && !module.embedVideo ? getEmbedUrl(module.video_url) : null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        
        <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }} 
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto z-10 flex flex-col">
          
          {module.embedVideo ? (
            <div className="relative w-full bg-black flex-shrink-0 group">
              <div dangerouslySetInnerHTML={{ __html: module.embedVideo }} />
              <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-black/50 hover:bg-black/80 rounded-lg flex items-center justify-center text-white transition-colors z-20">
                <SafeIcon icon={FiX} />
              </button>
            </div>
          ) : embedUrl ? (
            <div className="relative w-full aspect-video bg-black flex-shrink-0">
              <iframe
                src={embedUrl}
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={module.title}
              />
              <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-black/50 hover:bg-black/80 rounded-lg flex items-center justify-center text-white transition-colors z-20">
                <SafeIcon icon={FiX} />
              </button>
            </div>
          ) : (
            <div className={`h-40 bg-[#0f172a] relative flex items-center justify-center flex-shrink-0`}>
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm border border-white/5">
                <SafeIcon icon={Icon} className="text-white text-4xl opacity-90" />
              </div>
              
              <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg flex items-center justify-center text-white transition-colors">
                <SafeIcon icon={FiX} />
              </button>
              
              <div className="absolute bottom-4 left-6">
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/10 text-white backdrop-blur-sm border border-white/10">
                  {module.deliveryFormat}
                </span>
              </div>
            </div>
          )}

          <div className="p-6 space-y-5">
            <div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h2 className="text-xl font-bold text-slate-800">{module.title}</h2>
                <span className={`px-2.5 py-0.5 rounded-full border text-xs font-semibold flex-shrink-0 bg-slate-50 text-slate-600 border-slate-200`}>
                  {module.difficultyLevel}
                </span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{module.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-0 bg-slate-50 rounded-xl border border-slate-200 px-4 divide-y divide-slate-100">
              <InfoRow icon={FiClock} label="Duration" value={module.duration} />
              <InfoRow icon={FiBookOpen} label="Category" value={module.category} />
              <InfoRow icon={FiShield} label="Compliance Topic" value={module.linkedComplianceTopic} />
              <InfoRow icon={FiBarChart2} label="Total Uses" value={`${module.usageCount} campaign${module.usageCount !== 1 ? 's' : ''}`} />
              <InfoRow icon={FiCalendar} label="Last Used" value={module.lastUsed || 'Never used'} />
              <InfoRow icon={FiCalendar} label="Created" value={module.createdAt} />
            </div>

            {/* Compliance Tags */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Compliance Tags</p>
              <div className="flex flex-wrap gap-2">
                {(module.complianceTags || []).map(tag => (
                  <span key={tag} className="flex items-center gap-1 text-xs bg-slate-50 text-slate-600 border border-slate-200 px-2.5 py-1 rounded-full font-medium">
                    <SafeIcon icon={FiTag} className="text-xs" />{tag}
                  </span>
                ))}
                {(module.complianceTags || []).length === 0 && <span className="text-xs text-slate-400">None</span>}
              </div>
            </div>

            {/* Assigned Campaigns */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Assigned Campaigns</p>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400"><SafeIcon icon={FiLoader} className="animate-spin" /> Loading campaigns...</div>
              ) : campaigns.length === 0 ? (
                <p className="text-sm text-slate-400">Not assigned to any campaigns yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {campaigns.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                      <span className="text-sm font-medium text-slate-700">{c.campaignName}</span>
                      <span className="text-xs text-slate-400">{c.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => { onAssign(module); onClose(); }} disabled={module.status !== 'Active'} className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Assign to Campaign
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ModulePreviewModal;